import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import {
  Component,
  DestroyRef,
  ElementRef,
  Inject,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { delay } from "rxjs";

import { WINDOW } from "@bitwarden/angular/services/injection-tokens";
import { AbstractThemingService } from "@vault/angular/services/theming/theming.service.abstraction";

import { ApiService } from "@bitwarden/common/abstractions/api.service";
import { CryptoService } from "@bitwarden/common/abstractions/crypto.service";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { KeyConnectorService } from "@bitwarden/common/abstractions/keyConnector.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { PlatformUtilsService } from "@bitwarden/common/abstractions/platformUtils.service";
import { StateService } from "@bitwarden/common/abstractions/state.service";
import { UpdateProfileRequest } from "@bitwarden/common/models/request/update-profile.request";
import { ProfileResponse } from "@bitwarden/common/models/response/profile.response";

@Component({
  selector: "app-profile",
  templateUrl: "profile.component.html",
})
export class ProfileComponent implements OnInit {
  @ViewChild("avatarFileInput") avatarFileInput!: ElementRef<HTMLInputElement>;

  loading = true;
  profile: ProfileResponse;
  fingerprint: string;
  avatarSrc: string | null = null;
  protected avatarBackgroundColor: string | undefined;
  private userId!: string;
  protected readonly event = event;

  formPromise: Promise<any>;

  constructor(
    private apiService: ApiService,
    private i18nService: I18nService,
    private platformUtilsService: PlatformUtilsService,
    private cryptoService: CryptoService,
    private logService: LogService,
    private stateService: StateService,
    private keyConnectorService: KeyConnectorService,
    private router: Router,
    @Inject(WINDOW) private win: Window,
    private themingService: AbstractThemingService,
    @Inject(DOCUMENT) private document: Document,
    private destroyRef: DestroyRef,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {}

  async ngOnInit() {
    // Read before the profile fetch (and its awaited steps below) so bit-avatar never renders
    // with the old id-derived background color for a frame once `profile` becomes truthy.
    this.avatarBackgroundColor = this.readAvatarTokenColor();
    // delay(0) defers until after the theme class has been committed to the DOM by the
    // theming service. CSS custom properties are not transitionable, so their values
    // change atomically with the class swap — one macrotask is always sufficient.
    this.themingService.theme$.pipe(delay(0), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.avatarBackgroundColor = this.readAvatarTokenColor();
    });

    this.profile = await this.apiService.getProfile();
    this.loading = false;
    this.userId = await this.stateService.getUserId();
    const fingerprint = await this.cryptoService.getFingerprint(this.userId);
    if (fingerprint != null) {
      this.fingerprint = fingerprint.join("-");
    }
    this.avatarSrc = localStorage.getItem(`avatar_${this.userId}`);
  }

  private readAvatarTokenColor(): string | undefined {
    if (!isPlatformBrowser(this.platformId)) {
      return undefined;
    }
    const tokenColor = getComputedStyle(this.document.documentElement)
      .getPropertyValue("--color-surface-sunken")
      .trim();
    return tokenColor || undefined;
  }

  onNameChange(name: string) {
    this.profile.name = name.trim();
  }

  openAvatarFilePicker() {
    this.avatarFileInput.nativeElement.click();
  }

  protected copyFingerprint() {
    this.platformUtilsService.copyToClipboard(this.fingerprint, { window: this.win });
    this.platformUtilsService.showToast(
      "success",
      null,
      this.i18nService.t("valueCopied", this.i18nService.t("yourAccountsFingerprint")),
    );
  }

  private static readonly AVATAR_MAX_BYTES = 2 * 1024 * 1024; // 2 MB

  async onAvatarFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file || !file.type.startsWith("image/")) {
      return;
    }
    if (file.size > ProfileComponent.AVATAR_MAX_BYTES) {
      this.platformUtilsService.showToast("error", "", this.i18nService.t("avatarFileTooLarge"));
      return;
    }
    try {
      const dataUrl = await this.compressToDataUrl(file);
      this.avatarSrc = dataUrl;
      localStorage.setItem(`avatar_${this.userId}`, dataUrl);
    } catch {
      this.platformUtilsService.showToast("error", "", this.i18nService.t("errorOccurred"));
    }
    input.value = "";
  }

  private compressToDataUrl(file: File, maxSide = 200, quality = 0.8): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("canvas context unavailable"));
          return;
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = url;
    });
  }

  async submit() {
    if (!this.profile.name) {
      return;
    }

    try {
      const request = new UpdateProfileRequest(this.profile.name, this.profile.masterPasswordHint);
      this.formPromise = this.apiService.putProfile(request);
      await this.formPromise;
      this.platformUtilsService.showToast("success", null, this.i18nService.t("accountUpdated"));
    } catch (e) {
      this.logService.error(e);
    }
  }
}
